import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import {
  CircleNotch, WarningCircle, ArrowLeft, EnvelopeSimple, Phone, DeviceMobile,
  HardHat, Briefcase, Info, Clock, CurrencyDollar, ChartBar, DownloadSimple,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  NOT_STARTED: "#6B7280", IN_PROGRESS: "#0055FF", COMPLETED: "#059669",
  ARCHIVED: "#9CA3AF", INVOICED: "#7C3AED", CANCELLED: "#DC2626",
};
const sc = (s) => STATUS_COLORS[(s || "").toUpperCase()] || "#6B7280";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "—";
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`);

function Avatar({ src, name }) {
  const initials = (name || "?").split(" ").map((x) => x[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
  return src
    ? <img src={src} alt="" className="h-16 w-16 rounded-sm object-cover" />
    : <div className="flex h-16 w-16 items-center justify-center rounded-sm bg-[#0A0A0A] text-xl font-bold text-white">{initials}</div>;
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2.5 py-2">
      <Icon size={16} className="shrink-0 text-neutral-400" />
      <span className="text-xs uppercase tracking-wider text-neutral-400 w-16">{label}</span>
      <span className="text-sm font-medium text-[#0A0A0A] break-all">{value}</span>
    </div>
  );
}

export default function TechnicianDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [ts, setTs] = useState(null);
  const [tsStart, setTsStart] = useState("");
  const [tsEnd, setTsEnd] = useState("");

  const loadTs = (start = "", end = "") => {
    setTs(null);
    api.get(`/technicians/${id}/timesheets`, { params: { start_date: start, end_date: end } })
      .then((r) => setTs(r.data))
      .catch(() => setTs({ entries: [], totals: {}, by_worktype: [], matched_on: null }));
  };

  useEffect(() => {
    api.get(`/technicians/${id}`).then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
    loadTs();
  }, [id]);

  const exportTs = async () => {
    try {
      const res = await api.get(`/technicians/${id}/timesheets/export`, { params: { start_date: tsStart, end_date: tsEnd }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "timesheets.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Timesheet CSV export failed", e); }
  };

  if (error) return <Layout><div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-600" data-testid="tech-error"><WarningCircle size={28} className="text-[#D97706]" />{error}</div></Layout>;
  if (!d) return <Layout><div className="flex h-64 items-center justify-center" data-testid="tech-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;

  const t = d.technician;

  return (
    <Layout>
      <div data-testid="technician-detail-page">
        <button onClick={() => navigate("/technicians")} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-neutral-400 hover:text-[#0055FF]"><ArrowLeft size={16} /> All technicians</button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Personal info */}
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="tech-info">
            <div className="flex items-center gap-4">
              <Avatar src={t.avatar} name={t.name} />
              <div className="min-w-0">
                <h1 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">{t.name}</h1>
                <p className="flex items-center gap-1.5 text-sm text-neutral-500"><HardHat size={14} /> {t.position || "—"}</p>
                {t.isArchived && <span className="mt-1 inline-block rounded-sm bg-neutral-200 px-2 py-0.5 text-xs font-semibold text-neutral-600">Archived</span>}
              </div>
            </div>
            <div className="mt-4 divide-y divide-neutral-100">
              <Row icon={EnvelopeSimple} label="Email" value={t.email} />
              <Row icon={DeviceMobile} label="Mobile" value={t.mobile} />
              <Row icon={Phone} label="Phone" value={t.phone} />
            </div>
            <div className="mt-4 rounded-sm bg-[#0055FF]/[0.04] p-3">
              <p className="text-xs uppercase tracking-widest font-semibold text-neutral-500">Recent jobs assigned</p>
              <p className="font-heading text-3xl font-black tracking-tighter text-[#0055FF]">{d.job_count}</p>
            </div>
          </div>

          {/* Jobs list */}
          <div className="lg:col-span-2 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="tech-jobs">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-5 py-3">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight"><Briefcase size={18} /> Assigned Jobs</h2>
              <span className="text-xs text-neutral-400">indexed {fmtTime(d.index?.as_of)}</span>
            </div>
            {d.jobs.length === 0 ? (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-neutral-500" data-testid="tech-no-jobs">
                <Info size={24} className="text-neutral-300" />
                {d.index?.syncing ? "Job index is still building — check back in a minute." : "No recent jobs found for this technician."}
              </div>
            ) : (
              <div className="max-h-[600px] overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-neutral-50">
                    <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                      <th className="px-4 py-2.5 text-left">Job #</th>
                      <th className="px-4 py-2.5 text-left">Description</th>
                      <th className="px-4 py-2.5 text-left">Client</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Due</th>
                      <th className="px-4 py-2.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.jobs.map((j) => (
                      <tr key={j.id} className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03]" onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`tech-job-${j.jobNumber}`}>
                        <td className="px-4 py-2.5 font-mono font-semibold text-[#0055FF]">{j.jobNumber}</td>
                        <td className="max-w-[220px] truncate px-4 py-2.5" title={j.name}>{j.name || "—"}</td>
                        <td className="max-w-[160px] truncate px-4 py-2.5 text-neutral-600">{j.client || "—"}</td>
                        <td className="px-4 py-2.5 text-neutral-600">{j.taskType || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-neutral-600">{j.dueDate || "—"}</td>
                        <td className="px-4 py-2.5"><span className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: sc(j.status) + "1a", color: sc(j.status) }}>{j.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Timesheets (Legacy API) */}
        <div className="mt-6 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="tech-timesheets">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-5 py-3">
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight"><Clock size={18} /> Timesheets <span className="text-xs font-medium text-neutral-400">· Legacy API</span></h2>
            <div className="flex flex-wrap items-center gap-2">
              {ts?.matched_on === "surname" && <span className="rounded-sm bg-[#D97706]/10 px-2 py-0.5 text-xs font-semibold text-[#D97706]">matched by surname</span>}
              <input data-testid="tech-ts-start" type="date" value={tsStart} onChange={(e) => setTsStart(e.target.value)} className="rounded-sm border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-[#0055FF]" />
              <span className="text-xs text-neutral-400">to</span>
              <input data-testid="tech-ts-end" type="date" value={tsEnd} onChange={(e) => setTsEnd(e.target.value)} className="rounded-sm border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-[#0055FF]" />
              <button data-testid="tech-ts-apply" onClick={() => loadTs(tsStart, tsEnd)} className="rounded-sm bg-[#0055FF] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0044CC]">Apply</button>
              {(tsStart || tsEnd) && <button data-testid="tech-ts-clear" onClick={() => { setTsStart(""); setTsEnd(""); loadTs(); }} className="rounded-sm border border-neutral-300 px-2 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-50">Clear</button>}
              <button data-testid="tech-ts-export" onClick={exportTs} className="inline-flex items-center gap-1.5 rounded-sm bg-[#0A0A0A] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0055FF]"><DownloadSimple size={13} weight="bold" /> CSV</button>
            </div>
          </div>

          {ts === null ? (
            <div className="flex h-32 items-center justify-center"><CircleNotch size={22} className="animate-spin text-[#0055FF]" /></div>
          ) : !ts.entries?.length ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-neutral-500" data-testid="tech-no-timesheets">
              <Info size={22} className="text-neutral-300" />
              {ts.sync?.total ? "No timesheets matched this technician." : "No timesheets synced yet — run \u201cSync timesheets\u201d on the Technicians page."}
            </div>
          ) : (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 gap-px bg-neutral-200 sm:grid-cols-4" data-testid="tech-ts-kpis">
                {[
                  { icon: Clock, label: "Total hours", value: ts.totals.hours?.toLocaleString() },
                  { icon: ChartBar, label: "Productive hrs", value: ts.totals.productive_hours?.toLocaleString() },
                  { icon: CurrencyDollar, label: "Total charged", value: money(ts.totals.charge) },
                  { icon: Briefcase, label: "Entries", value: ts.totals.count?.toLocaleString() },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-white p-4">
                    <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-400"><kpi.icon size={13} weight="bold" />{kpi.label}</p>
                    <p className="mt-1 font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">{kpi.value ?? "—"}</p>
                  </div>
                ))}
              </div>
              <p className="px-5 pt-3 text-xs text-neutral-400">
                {ts.totals.first_date} → {ts.totals.last_date}
                {ts.by_worktype?.length > 0 && <> · {ts.by_worktype.slice(0, 4).map((w) => `${w.worktype} ${w.hours}h`).join(" · ")}</>}
              </p>

              <div className="mt-2 max-h-[460px] overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-neutral-50">
                    <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                      <th className="px-4 py-2.5 text-left">Work date</th>
                      <th className="px-4 py-2.5 text-left">Job #</th>
                      <th className="px-4 py-2.5 text-left">Task</th>
                      <th className="px-4 py-2.5 text-left">Work type</th>
                      <th className="px-4 py-2.5 text-right">Hours</th>
                      <th className="px-4 py-2.5 text-right">Charge</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ts.entries.map((e, i) => (
                      <tr key={e.timesheetid || i} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`ts-row-${i}`}>
                        <td className="px-4 py-2.5 font-mono text-neutral-700">{e.workdate || "—"}</td>
                        <td className="px-4 py-2.5">
                          {e.jobnumber ? <button onClick={() => navigate(`/jobs?q=${e.jobnumber}`)} className="font-mono font-semibold text-[#0055FF] hover:underline">{e.jobnumber}</button> : <span className="text-neutral-400">—</span>}
                        </td>
                        <td className="max-w-[220px] truncate px-4 py-2.5 text-neutral-600" title={e.taskname}>{e.taskname || "—"}</td>
                        <td className="px-4 py-2.5 text-neutral-600" title={e.worktype_desc}>{e.worktype || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{e.hours}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{money(e.charge)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${e.type === "Productive" ? "bg-[#059669]/10 text-[#059669]" : "bg-neutral-100 text-neutral-600"}`}>{e.type || "—"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <PageAdvice title="About this technician's timesheets" items={[
          "Timesheets come from the Legacy Aroflo API and are matched to this person by exact Legacy user ID where available, otherwise by name (a \u201cmatched by surname\u201d tag appears when only the surname could be matched).",
          "Coverage limit: the Legacy users zone currently exposes only 6 active users, so exact-ID matching is limited to those; everyone else is name-matched. If GBE can expose more legacy users (or a user-ID\u2194email export), exact matching extends to all historical staff.",
          "Totals cover all synced timesheets; run \u201cSync timesheets\u201d on the Technicians page to refresh.",
        ]} />
      </div>
    </Layout>
  );
}
